import { useEffect, useMemo, useRef, useState } from "react";
import {
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileText,
  Info,
  Loader,
  Plus,
  ShieldCheck,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { FormSelect } from "../../../components/Modal";
import {
  addSspControlImplementation,
  bulkUpdateImplementedRequirements,
  saveValidationDocument,
  type ImplementationStatus,
  type SspComponentResponse,
} from "../../../../api/frameworksApi";
import { ValidationDocPicker } from "../../../components/ValidationDocPicker";
import { useToastStore } from "../../../../stores/toastStore";

/* ── Shared configuration ────────────────────────────────────────── */

const STATUS_COLOR: Record<string, string> = {
  implemented:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  partial:
    "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  planned: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  alternative:
    "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400",
  "not-applicable": "bg-muted text-muted-foreground",
};

const RESPONSIBLE_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: "administrator", label: "Administrator" },
  { value: "isso", label: "ISSO" },
  { value: "auditor", label: "Auditor" },
  { value: "ciso", label: "CISO" },
  { value: "grc-manager", label: "GRC Manager" },
  { value: "assessor", label: "Assessor" },
  { value: "lead-assessor", label: "Lead Assessor" },
  { value: "system-owner", label: "System Owner" },
];

type ExportMethod = "fully_provided" | "shared" | "not_applicable" | "";
type Section = "component" | "export" | "validation";

const EXPORT_METHOD_OPTIONS: {
  value: Exclude<ExportMethod, "">;
  label: string;
  icon: typeof ShieldCheck;
  accent: "emerald" | "amber" | "slate";
  tooltip: string;
}[] = [
  {
    value: "fully_provided",
    label: "Fully Provided",
    icon: ShieldCheck,
    accent: "emerald",
    tooltip: "Service covers 100%, app inherits and does nothing.",
  },
  {
    value: "shared",
    label: "Shared",
    icon: Users,
    accent: "amber",
    tooltip: "Service covers their part, app must also add their statement.",
  },
  {
    value: "not_applicable",
    label: "Not Applicable",
    icon: Ban,
    accent: "slate",
    tooltip: "Clears/removes the export for that control.",
  },
];

const IMPL_STATUS_OPTIONS = [
  { value: "planned", label: "Planned" },
  { value: "partial", label: "Partial" },
  { value: "implemented", label: "Implemented" },
  { value: "alternative", label: "Alternative" },
  { value: "not-applicable", label: "Not Applicable" },
] as const;

/* ── Draft state ─────────────────────────────────────────────────── */

interface ComponentDraft {
  draftId: string;
  componentUuid: string;
  description: string;
  status: string;
  exportMethod: ExportMethod;
  exportDescription: string;
  responsibleRole: string;
  validationDocId: string;
  validationDocName: string;
}

let draftIdSeq = 0;
const makeDraft = (seedUuid: string): ComponentDraft => ({
  draftId: `d-${++draftIdSeq}-${Date.now()}`,
  componentUuid: seedUuid,
  description: "",
  status: "planned",
  exportMethod: "",
  exportDescription: "",
  responsibleRole: "",
  validationDocId: "",
  validationDocName: "",
});

const isDraftConfigured = (d: ComponentDraft) =>
  !!d.componentUuid && !!d.status;

/* ── Props ───────────────────────────────────────────────────────── */

export interface AddComponentsWizardProps {
  sspId: string;
  ciId?: string;
  controlId: string;
  controlTitle?: string;
  controlStatement?: string;
  irSortOrder?: number;
  disabledComponentUuids?: string[];
  sspComponents: SspComponentResponse[];
  /**
   * Pre-filled drafts when opening in edit mode. The wizard seeds the draft
   * list with these; users can still add more or remove them.
   */
  initialDrafts?: Array<{
    componentUuid: string;
    description?: string | null;
    status?: string | null;
    exportMethod?: ExportMethod;
    exportDescription?: string | null;
    responsibleRole?: string | null;
    validationDocId?: string | null;
    validationDocName?: string | null;
  }>;
  /** Custom label for the primary action. Defaults to "Add to List". */
  saveLabel?: string;
  onSaved: () => void;
  onCancel: () => void;
}

/* ── Multi-select chip picker ────────────────────────────────────── */

function MultiSelectChips({
  selectedUuids,
  options,
  disabledUuids,
  onAdd,
  onRemove,
  placeholder = "Select components…",
  openTrigger = 0,
}: {
  selectedUuids: string[];
  options: SspComponentResponse[];
  disabledUuids: Set<string>;
  onAdd: (uuid: string) => void;
  onRemove: (uuid: string) => void;
  placeholder?: string;
  openTrigger?: number;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      if (
        rootRef.current &&
        !rootRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  // Open dropdown when parent requests it via trigger
  useEffect(() => {
    if (openTrigger > 0) {
      setOpen(true);
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [openTrigger]);

  const selectedSet = new Set(selectedUuids);

  return (
    <div ref={rootRef} className="relative">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className="w-full min-h-[46px] text-left rounded-lg border border-indigo-300 dark:border-indigo-500/40 bg-background focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors px-3 py-2 flex items-center gap-2 flex-wrap cursor-pointer"
      >
        {selectedUuids.length === 0 && (
          <span className="text-sm text-muted-foreground">{placeholder}</span>
        )}
        {selectedUuids.map((uuid) => {
          const c = options.find((o) => o.componentUuid === uuid);
          if (!c) return null;
          return (
            <span
              key={uuid}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/30 px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-200"
            >
              {c.title} ({c.componentType})
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(uuid);
                }}
                className="rounded hover:bg-background/50 p-0.5"
                title="Remove"
              >
                <X className="size-3" />
              </button>
            </span>
          );
        })}
        <ChevronDown
          className={`ml-auto size-4 text-muted-foreground flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>

      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 max-h-64 overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
          {options.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-foreground text-center">
              No components available.
            </div>
          ) : (
            options.map((c) => {
              const selected = selectedSet.has(c.componentUuid);
              const disabled =
                !selected && disabledUuids.has(c.componentUuid);
              return (
                <button
                  key={c.componentUuid}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    if (selected) onRemove(c.componentUuid);
                    else onAdd(c.componentUuid);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                    disabled
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-muted cursor-pointer"
                  } ${selected ? "bg-indigo-50 dark:bg-indigo-500/10" : ""}`}
                >
                  <span
                    className={`size-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                      selected
                        ? "bg-indigo-600 border-indigo-600"
                        : "border-border"
                    }`}
                  >
                    {selected && (
                      <CheckCircle2 className="size-3 text-white" />
                    )}
                  </span>
                  <span className="text-foreground font-medium">
                    {c.title}
                  </span>
                  <span className="text-muted-foreground">
                    ({c.componentType})
                  </span>
                  {disabled && (
                    <span className="ml-auto text-[10px] text-muted-foreground">
                      already added
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

/* ── Component ───────────────────────────────────────────────────── */

export function AddComponentsWizard({
  sspId,
  ciId,
  controlId,
  controlTitle = "",
  controlStatement = "",
  irSortOrder = 0,
  disabledComponentUuids = [],
  sspComponents,
  initialDrafts,
  saveLabel = "Save",
  onSaved,
  onCancel,
}: AddComponentsWizardProps) {
  // Build the seeded draft list once so both drafts + expandedDraftIds can use it
  const seededDrafts = useMemo<ComponentDraft[]>(
    () =>
      (initialDrafts ?? []).map((seed) => ({
        draftId: `d-${++draftIdSeq}-${Date.now()}`,
        componentUuid: seed.componentUuid,
        description: seed.description ?? "",
        status: seed.status ?? "planned",
        exportMethod: seed.exportMethod ?? "",
        exportDescription: seed.exportDescription ?? "",
        responsibleRole: seed.responsibleRole ?? "",
        validationDocId: seed.validationDocId ?? "",
        validationDocName: seed.validationDocName ?? "",
      })),
    // Only seed on mount — subsequent prop changes shouldn't wipe user edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [drafts, setDrafts] = useState<ComponentDraft[]>(() => seededDrafts);
  const [pendingUuids, setPendingUuids] = useState<string[]>([]);
  const [expandedDraftIds, setExpandedDraftIds] = useState<Set<string>>(
    () =>
      new Set(
        seededDrafts.length > 0 ? [seededDrafts[0].draftId] : [],
      ),
  );
  const [innerTabs, setInnerTabs] = useState<Record<string, Section>>({});
  const [saving, setSaving] = useState(false);

  const parentDisabled = useMemo(
    () => new Set(disabledComponentUuids),
    [disabledComponentUuids],
  );

  const totalConfigured = drafts.filter(isDraftConfigured).length;

  function updateDraft(id: string, patch: Partial<ComponentDraft>) {
    setDrafts((prev) =>
      prev.map((d) => (d.draftId === id ? { ...d, ...patch } : d)),
    );
  }

  function toggleExpanded(id: string) {
    setExpandedDraftIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function addDraftByUuid(uuid: string) {
    // Ignore if a draft for that component already exists
    if (drafts.some((d) => d.componentUuid === uuid)) return;
    const d = makeDraft(uuid);
    setDrafts((prev) => [...prev, d]);
    setExpandedDraftIds((prev) => new Set(prev).add(d.draftId));
    setInnerTabs((prev) => ({ ...prev, [d.draftId]: "component" }));
  }

  function handlePickerAdd(uuid: string) {
    if (drafts.some((d) => d.componentUuid === uuid)) return;
    setPendingUuids((prev) => (prev.includes(uuid) ? prev : [...prev, uuid]));
  }

  function handlePickerRemove(uuid: string) {
    if (pendingUuids.includes(uuid)) {
      setPendingUuids((prev) => prev.filter((u) => u !== uuid));
      return;
    }
    removeDraftByUuid(uuid);
  }

  function handleAddToList() {
    if (pendingUuids.length === 0) {
      useToastStore
        .getState()
        .showInfo(
          "No selection",
          "Select components from the dropdown first.",
        );
      return;
    }
    pendingUuids.forEach((uuid) => addDraftByUuid(uuid));
    setPendingUuids([]);
  }

  function removeDraft(id: string) {
    setDrafts((prev) => prev.filter((d) => d.draftId !== id));
    setExpandedDraftIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setInnerTabs((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function removeDraftByUuid(uuid: string) {
    const target = drafts.find((d) => d.componentUuid === uuid);
    if (target) removeDraft(target.draftId);
  }

  async function handleSave() {
    const usable = drafts.filter((d) => d.componentUuid);
    if (usable.length === 0) {
      useToastStore
        .getState()
        .showError(
          "Nothing to save",
          "Select at least one component to add.",
        );
      return;
    }
    setSaving(true);
    try {
      let effectiveCiId = ciId;
      if (!effectiveCiId) {
        const created = await addSspControlImplementation(sspId, {
          source: "profile",
          controlId,
          implementationStatus: (usable[0].status ||
            "planned") as ImplementationStatus,
        });
        effectiveCiId = created?.data?.id;
        if (!effectiveCiId) {
          throw new Error(
            "Failed to create a control implementation record.",
          );
        }
      }

      await bulkUpdateImplementedRequirements(sspId, effectiveCiId, [
        {
          controlId,
          implementationStatus: (usable[0].status ||
            "planned") as ImplementationStatus,
          byComponents: usable.map((d, idx) => ({
            componentUuid: d.componentUuid,
            description: d.description.trim() || undefined,
            implementationStatus: d.status || undefined,
            exportMethod: d.exportMethod || undefined,
            provided:
              d.exportMethod && d.exportMethod !== "not_applicable"
                ? {
                    description: d.exportDescription.trim() || undefined,
                    responsibleRoles: d.responsibleRole
                      ? [{ "role-id": d.responsibleRole }]
                      : undefined,
                  }
                : undefined,
            sortOrder: idx,
          })),
          sortOrder: irSortOrder,
        },
      ]);

      // Persist the validation document link for every draft that has one
      // selected. Best-effort — a failure here shouldn't undo the
      // implementation save that already succeeded above.
      const withDocs = usable.filter((d) => d.validationDocId);
      if (withDocs.length > 0) {
        const results = await Promise.allSettled(
          withDocs.map((d) => {
            const componentId = sspComponents.find(
              (c) => c.componentUuid === d.componentUuid,
            )?.id;
            if (!componentId) return Promise.reject(new Error("Unknown component"));
            return saveValidationDocument(sspId, effectiveCiId as string, {
              componentId,
              validationDocumentId: d.validationDocId,
              validationDocumentName: d.validationDocName,
            });
          }),
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          useToastStore
            .getState()
            .showWarning(
              "Validation document not linked",
              `${failed} validation document link${failed === 1 ? "" : "s"} could not be saved.`,
            );
        }
      }

      useToastStore
        .getState()
        .showSuccess(
          "Added",
          `${usable.length} component${usable.length === 1 ? "" : "s"} added to this control.`,
        );
      onSaved();
    } catch (err: any) {
      const httpStatus = err?.response?.status ?? err?.status;
      const detail = err?.response?.data?.errors?.[0]?.detail;
      const msg =
        httpStatus === 409
          ? "One of these components is already implemented under this control."
          : httpStatus === 423
            ? "This SSP is frozen and cannot be edited."
            : detail ?? (err instanceof Error ? err.message : "Failed to save");
      useToastStore.getState().showError("Error", msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4 min-h-0 flex-1 overflow-hidden">
        {/* ── LEFT: draft summary cards ── */}
        <aside className="flex flex-col gap-3 min-h-0 overflow-y-auto pr-1">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-foreground">
                Selected Components
              </h3>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                {drafts.length}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
              Add one or more components and configure their details.
            </p>
          </div>

          <div className="space-y-2">
            {drafts.length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-4 text-center">
                <p className="text-xs text-muted-foreground">
                  No components selected yet
                </p>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Pick components from the dropdown and click "Add to List"
                </p>
              </div>
            )}
            {drafts.map((d) => {
              const meta = sspComponents.find(
                (c) => c.componentUuid === d.componentUuid,
              );
              const expanded = expandedDraftIds.has(d.draftId);
              const configured = isDraftConfigured(d);
              return (
                <div
                  key={d.draftId}
                  onClick={() => {
                    if (!expandedDraftIds.has(d.draftId))
                      toggleExpanded(d.draftId);
                  }}
                  className={`rounded-lg border p-3 cursor-pointer transition-all ${
                    expanded
                      ? "border-indigo-400 dark:border-indigo-500/60 bg-indigo-50/40 dark:bg-indigo-500/10 ring-2 ring-indigo-100 dark:ring-indigo-500/20"
                      : "border-border bg-card hover:border-indigo-300 dark:hover:border-indigo-500/40"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className="size-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                      <FileText className="size-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground truncate">
                        {meta?.title ?? "Unnamed component"}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        ({meta?.componentType ?? "—"})
                      </p>
                      {d.status && (
                        <span
                          className={`inline-flex mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${STATUS_COLOR[d.status] ?? "bg-muted text-muted-foreground"} capitalize`}
                        >
                          {d.status.replace(/-/g, " ")}
                        </span>
                      )}
                    </div>
                    {configured && (
                      <CheckCircle2 className="size-4 text-emerald-500 flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                    <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      Component details <ChevronRight className="size-3" />
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeDraft(d.draftId);
                      }}
                      className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title="Remove this component"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-lg bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 p-3 flex gap-2 items-start">
            <Info className="size-3.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-indigo-800 dark:text-indigo-200 leading-relaxed">
              You can add multiple components and configure details for each of
              them.
            </p>
          </div>
        </aside>

        {/* ── RIGHT: multi-select + accordion ── */}
        <section className="flex flex-col min-h-0 overflow-y-auto pr-1">
          {/* 1. Select Component — multi-select chip picker */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-foreground mb-2">
              1. Select Component
            </label>
            <MultiSelectChips
              selectedUuids={[
                ...drafts.map((d) => d.componentUuid).filter(Boolean),
                ...pendingUuids,
              ]}
              options={sspComponents}
              disabledUuids={parentDisabled}
              onAdd={handlePickerAdd}
              onRemove={handlePickerRemove}
              placeholder="Select components…"
            />
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Select one or more components to configure the details.
            </p>
          </div>

          {/* Accordion header */}
          {drafts.length > 0 && (
            <div className="rounded-lg border border-border">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h4 className="text-sm font-bold text-foreground">
                  Configure details for each component
                </h4>
                <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                  {totalConfigured} of {drafts.length} configured
                </span>
              </div>

              <div className="divide-y divide-border">
                {drafts.map((draft) => {
                  const meta = sspComponents.find(
                    (c) => c.componentUuid === draft.componentUuid,
                  );
                  const expanded = expandedDraftIds.has(draft.draftId);
                  const configured = isDraftConfigured(draft);
                  const innerTab = innerTabs[draft.draftId] ?? "component";
                  return (
                    <div key={draft.draftId}>
                      {/* Accordion header row */}
                      <button
                        type="button"
                        onClick={() => toggleExpanded(draft.draftId)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left"
                      >
                        {expanded ? (
                          <ChevronDown className="size-4 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronRight className="size-4 text-muted-foreground flex-shrink-0" />
                        )}
                        <div className="size-8 rounded-md bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                          <FileText className="size-4 text-indigo-600 dark:text-indigo-300" />
                        </div>
                        <span className="text-sm font-semibold text-foreground flex-1 truncate">
                          {meta?.title ?? "Component"}{" "}
                          <span className="font-normal text-muted-foreground">
                            ({meta?.componentType ?? "—"})
                          </span>
                        </span>
                        {configured && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                            <CheckCircle2 className="size-3" /> Configured
                          </span>
                        )}
                      </button>

                      {/* Accordion body */}
                      {expanded && (
                        <div className="px-4 pb-4">
                          {/* Inner tabs */}
                          <div className="flex items-center gap-1 border-b border-border mb-3">
                            {(
                              [
                                { key: "component" as Section, label: "Component" },
                                { key: "export" as Section, label: "Export" },
                                {
                                  key: "validation" as Section,
                                  label: "Validation Document",
                                },
                              ] as const
                            ).map((t) => (
                              <button
                                key={t.key}
                                type="button"
                                onClick={() =>
                                  setInnerTabs((prev) => ({
                                    ...prev,
                                    [draft.draftId]: t.key,
                                  }))
                                }
                                className={`px-3 py-1.5 text-[11px] font-semibold border-b-2 transition-colors ${
                                  innerTab === t.key
                                    ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                                    : "border-transparent text-muted-foreground hover:text-foreground"
                                }`}
                              >
                                {t.label}
                              </button>
                            ))}
                          </div>

                          {/* Per-draft section body */}
                          {innerTab === "component" && (
                            <ComponentSection
                              draft={draft}
                              onChange={(patch) =>
                                updateDraft(draft.draftId, patch)
                              }
                            />
                          )}
                          {innerTab === "export" && (
                            <ExportSection
                              draft={draft}
                              onChange={(patch) =>
                                updateDraft(draft.draftId, patch)
                              }
                            />
                          )}
                          {innerTab === "validation" && (
                            <ValidationSection
                              draft={draft}
                              onChange={(patch) =>
                                updateDraft(draft.draftId, patch)
                              }
                              controlId={controlId}
                              controlTitle={controlTitle}
                              controlStatement={controlStatement}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-border flex-shrink-0">
        <button
          type="button"
          onClick={handleAddToList}
          disabled={pendingUuids.length === 0}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold border border-indigo-300 dark:border-indigo-500/40 text-indigo-600 dark:text-indigo-300 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-500/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="size-4" />
          Add to List
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || drafts.length === 0}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {saving && <Loader className="size-4 animate-spin" />}
          {saveLabel}
        </button>
      </div>
    </div>
  );
}

/* ── Per-section forms ───────────────────────────────────────────── */

function ComponentSection({
  draft,
  onChange,
}: {
  draft: ComponentDraft;
  onChange: (patch: Partial<ComponentDraft>) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-semibold text-foreground mb-1.5">
          How does this component implement the control?
        </label>
        <textarea
          value={draft.description}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={4}
          placeholder="Describe how this component implements the control… e.g. 'The Internet Gateway enforces input validation at the perimeter by rejecting malformed requests via WAF rules defined in…'"
          className="w-full text-sm border border-border rounded-lg px-3 py-2.5 bg-background outline-none resize-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-foreground mb-2">
          Implementation status
        </label>
        <div className="flex flex-wrap gap-2">
          {IMPL_STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onChange({ status: opt.value })}
              className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-colors ${
                draft.status === opt.value
                  ? (STATUS_COLOR[opt.value] ?? "") + " border-transparent"
                  : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ExportSection({
  draft,
  onChange,
}: {
  draft: ComponentDraft;
  onChange: (patch: Partial<ComponentDraft>) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-semibold text-foreground mb-2">
          Provider responsibility
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {EXPORT_METHOD_OPTIONS.map((opt) => {
            const IconComp = opt.icon;
            const isOn = draft.exportMethod === opt.value;
            const activeClasses =
              opt.accent === "emerald"
                ? "border-emerald-400 bg-emerald-50 text-emerald-700 dark:border-emerald-500/60 dark:bg-emerald-500/10 dark:text-emerald-300"
                : opt.accent === "amber"
                  ? "border-amber-400 bg-amber-50 text-amber-700 dark:border-amber-500/60 dark:bg-amber-500/10 dark:text-amber-300"
                  : "border-slate-400 bg-slate-50 text-slate-700 dark:border-slate-500/60 dark:bg-slate-500/10 dark:text-slate-300";
            return (
              <button
                key={opt.value}
                onClick={() => onChange({ exportMethod: opt.value })}
                title={opt.tooltip}
                className={`group relative flex items-center gap-2 rounded-lg border p-3 text-left transition-all ${
                  isOn
                    ? activeClasses
                    : "border-border bg-background hover:bg-muted text-foreground"
                }`}
              >
                <IconComp className="size-4 flex-shrink-0" />
                <span className="text-xs font-semibold flex-1">
                  {opt.label}
                </span>
                <span
                  className="text-muted-foreground group-hover:text-foreground transition-colors"
                  title={opt.tooltip}
                >
                  <Info className="size-3.5" />
                </span>
                <span className="pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full opacity-0 group-hover:opacity-100 transition-opacity z-10 rounded-md bg-foreground text-background text-[11px] leading-snug px-2.5 py-1.5 shadow-md whitespace-normal w-56">
                  {opt.tooltip}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-foreground mb-1.5">
          Export description
        </label>
        <textarea
          value={draft.exportDescription}
          onChange={(e) => onChange({ exportDescription: e.target.value })}
          rows={4}
          placeholder="Describe what the provider handles for this control… e.g. 'EC2 Instances handle all network-layer input validation using AWS WAF.'"
          className="w-full text-sm border border-border rounded-lg px-3 py-2.5 bg-background outline-none resize-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-60"
          disabled={draft.exportMethod === "not_applicable"}
        />
        {draft.exportMethod === "not_applicable" && (
          <p className="mt-1 text-[11px] text-muted-foreground">
            Not Applicable clears the export — no description needed.
          </p>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-foreground mb-1.5">
          Responsible Role
        </label>
        <FormSelect
          value={draft.responsibleRole}
          onChange={(v) => onChange({ responsibleRole: v })}
          options={RESPONSIBLE_ROLE_OPTIONS}
          placeholder="Select a role…"
        />
      </div>
    </div>
  );
}

function ValidationSection({
  draft,
  onChange,
  controlId,
  controlTitle = "",
  controlStatement = "",
}: {
  draft: ComponentDraft;
  onChange: (patch: Partial<ComponentDraft>) => void;
  controlId: string;
  controlTitle?: string;
  controlStatement?: string;
}) {
  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
          Validation document <span className="text-red-500">*</span>
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
          Documents from the Content Registry scanned against this control.
          Pick one to validate during SSP execution.
        </p>
      </div>
      <ValidationDocPicker
        controlId={controlId}
        controlTitle={controlTitle}
        controlStatement={controlStatement}
        value={draft.validationDocId}
        onChange={(id, name) =>
          onChange({ validationDocId: id, validationDocName: name })
        }
      />
    </div>
  );
}
